import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUiStore } from '../../store/uiStore';
import { Spinner } from '../../components/Spinner';

export function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const pushToast = useUiStore((s) => s.pushToast);

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
      pushToast('success', 'Password reset email sent.');
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Could not send reset email',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-100">Check your email</h2>
        <p className="text-sm text-slate-300">
          If an account exists for{' '}
          <span className="font-semibold text-gold">{email}</span>, a password
          reset link is on its way.
        </p>
        <Link to="/login" className="btn-gold w-full">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-100">Reset password</h2>
      <p className="text-sm text-slate-300">
        Enter your email and we&apos;ll send you a reset link.
      </p>

      <div>
        <label className="label" htmlFor="forgot-email">
          Email
        </label>
        <input
          id="forgot-email"
          type="email"
          autoComplete="email"
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <button type="submit" className="btn-gold w-full" disabled={submitting}>
        {submitting ? <Spinner /> : 'Send reset link'}
      </button>

      <p className="text-center text-sm text-slate-400">
        <Link to="/login" className="hover:text-gold">
          Back to sign in
        </Link>
      </p>
    </form>
  );
}
